
import { KeyContact } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { logActivity } from './activityService';

export const getKeyContacts = async (leadId: string): Promise<KeyContact[]> => {
  try {
    const { data, error } = await supabase
      .from('lead_key_contacts')
      .select('*')
      .eq('lead_id', leadId)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching key contacts:', error);
      throw error;
    }

    // Map the database response to the KeyContact type
    const keyContacts: KeyContact[] = data.map(contact => ({
      id: contact.id,
      leadId: contact.lead_id,
      name: contact.name,
      position: contact.position || undefined,
      email: contact.email || undefined,
      phone: contact.phone || undefined,
      isPrimary: contact.is_primary,
      notes: contact.notes || undefined,
      createdAt: contact.created_at,
      createdBy: contact.created_by || undefined,
      linkedinUrl: (contact as any).linkedin_url || undefined,
      source: (contact as any).source || 'manual',
      status: (contact as any).status || 'active'
    }));

    return keyContacts;
  } catch (error) {
    console.error('Error in getKeyContacts:', error);
    return [];
  }
};

export const addKeyContact = async (
  leadId: string,
  contactData: Omit<KeyContact, 'id' | 'leadId' | 'createdAt' | 'createdBy'>
): Promise<KeyContact> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;

    const { data, error } = await supabase
      .from('lead_key_contacts')
      .insert({
        lead_id: leadId,
        name: contactData.name,
        position: contactData.position,
        email: contactData.email,
        phone: contactData.phone,
        is_primary: contactData.isPrimary,
        notes: contactData.notes,
        created_by: userId,
        status: contactData.status || 'active'
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding key contact:', error);
      throw error;
    }

    // Map the database response to the KeyContact type
    const keyContact: KeyContact = {
      id: data.id,
      leadId: data.lead_id,
      name: data.name,
      position: data.position || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      isPrimary: data.is_primary,
      notes: data.notes || undefined,
      createdAt: data.created_at,
      createdBy: data.created_by || undefined,
      status: (data as any).status || 'active'
    };

    // Log activity for adding a new contact
    await logActivity({
      leadId,
      type: 'other',
      title: `Added key contact: ${contactData.name}`,
      description: contactData.isPrimary ? 'Set as primary contact' : undefined,
      performedBy: userId
    });

    return keyContact;
  } catch (error) {
    console.error('Error in addKeyContact:', error);
    throw error;
  }
};

export const updateKeyContact = async (
  contactId: string,
  contactData: Partial<Omit<KeyContact, 'id' | 'leadId' | 'createdAt' | 'createdBy'>>
): Promise<KeyContact> => {
  try {
    // Get the current contact to compare changes for activity logging
    const { data: currentContact, error: fetchError } = await supabase
      .from('lead_key_contacts')
      .select('*')
      .eq('id', contactId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching current contact:', fetchError);
      throw fetchError;
    }

    // Convert from camelCase to snake_case for the database
    const dbData: any = {};
    if (contactData.name !== undefined) dbData.name = contactData.name;
    if (contactData.position !== undefined) dbData.position = contactData.position;
    if (contactData.email !== undefined) dbData.email = contactData.email;
    if (contactData.phone !== undefined) dbData.phone = contactData.phone;
    if (contactData.isPrimary !== undefined) dbData.is_primary = contactData.isPrimary;
    if (contactData.notes !== undefined) dbData.notes = contactData.notes;
    if (contactData.status !== undefined) dbData.status = contactData.status;

    const { data, error } = await supabase
      .from('lead_key_contacts')
      .update(dbData)
      .eq('id', contactId)
      .select()
      .single();

    if (error) {
      console.error('Error updating key contact:', error);
      throw error;
    }

    // Get the authenticated user for activity logging
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;

    // Map the database response to the KeyContact type
    const keyContact: KeyContact = {
      id: data.id,
      leadId: data.lead_id,
      name: data.name,
      position: data.position || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      isPrimary: data.is_primary,
      notes: data.notes || undefined,
      createdAt: data.created_at,
      createdBy: data.created_by || undefined,
      status: (data as any).status || 'active'
    };

    // Log activity for updating contact
    const activityDescription: string[] = [];
    
    if (contactData.name !== undefined && contactData.name !== currentContact.name) {
      activityDescription.push(`Name changed to "${contactData.name}"`);
    }
    
    if (contactData.position !== undefined && contactData.position !== currentContact.position) {
      activityDescription.push(`Position updated to "${contactData.position}"`);
    }
    
    if (contactData.email !== undefined && contactData.email !== currentContact.email) {
      activityDescription.push(`Email updated`);
    }
    
    if (contactData.phone !== undefined && contactData.phone !== currentContact.phone) {
      activityDescription.push(`Phone updated`);
    }
    
    // Primary contact status change is important enough for a specific message
    if (contactData.isPrimary !== undefined && contactData.isPrimary !== currentContact.is_primary) {
      if (contactData.isPrimary) {
        await logActivity({
          leadId: data.lead_id,
          type: 'other',
          title: `${data.name} set as primary contact`,
          performedBy: userId
        });
      } else {
        await logActivity({
          leadId: data.lead_id,
          type: 'other',
          title: `${data.name} removed as primary contact`,
          performedBy: userId
        });
      }
    }
    
    // Log general contact updates if any fields were changed
    if (activityDescription.length > 0) {
      await logActivity({
        leadId: data.lead_id,
        type: 'other',
        title: `Updated key contact: ${data.name}`,
        description: activityDescription.join(', '),
        performedBy: userId
      });
    }

    return keyContact;
  } catch (error) {
    console.error('Error in updateKeyContact:', error);
    throw error;
  }
};

export const deleteKeyContact = async (contactId: string): Promise<void> => {
  try {
    // Get contact details before deletion for activity logging
    const { data: contact, error: fetchError } = await supabase
      .from('lead_key_contacts')
      .select('*')
      .eq('id', contactId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching contact for deletion:', fetchError);
      throw fetchError;
    }

    const { error } = await supabase
      .from('lead_key_contacts')
      .delete()
      .eq('id', contactId);

    if (error) {
      console.error('Error deleting key contact:', error);
      throw error;
    }
    
    // Get the authenticated user for activity logging
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    // Log contact deletion activity
    await logActivity({
      leadId: contact.lead_id,
      type: 'other',
      title: `Removed key contact: ${contact.name}`,
      description: contact.is_primary ? 'Was primary contact' : undefined,
      performedBy: userId
    });
  } catch (error) {
    console.error('Error in deleteKeyContact:', error);
    throw error;
  }
};
